import type React from "react"

/**
 * Home's own design tokens.
 *
 * Scoped deliberately: Home is the one screen the client signed off pixel by
 * pixel, and its muted palette is not the rest of the app's. Nothing here is
 * imported outside `DashboardPage/` — the shared widget tokens in
 * `components/widgets/tokens` still own every other surface.
 */
export const homeColor = {
  canvas: "#F6F7F8",
  surface: "#FFFFFF",
  ink: "#29343D",
  inkMuted: "#687580",
  // #687580 measures 4.4:1 on the #F6F7F8 canvas — fine on white, a hair under
  // AA on the ground. Same hue, one step darker, for canvas-level copy.
  inkMutedOnCanvas: "#5E6B76",
  teal: "#527F7D",
  tealHover: "#426B69",
  tealPale: "#EAF1EF",
  iconBg: "#F0F5F3",
  iconFg: "#6E9690",
  border: "#E8ECEE",
  warningBg: "#F7F2EA",
  warningFg: "#AA8C61",
  negative: "#A84F59",
  chartSage: "#9CB9AE",
  chartRose: "#C69B9D",
  progressFill: "#8AAEA5",
  progressTrack: "#EDF0F2",
  chipBg: "#F0F3F4",
  chipFg: "#53636D"
} as const

// Inter is loaded alongside Manrope in index.html; Home opts into it through
// the `.home-canvas` rule rather than moving the app-wide theme token.
export const HOME_FONT_STACK = "Inter, Arial, sans-serif"

/** Money and percentages line up column to column only with tabular figures. */
export const TABULAR: React.CSSProperties = {
  fontVariantNumeric: "tabular-nums",
  fontFeatureSettings: '"tnum" 1'
}

export const homeText = {
  welcomeTitle: { fontSize: 31, lineHeight: "38px", fontWeight: 700 },
  welcomeSubtitle: { fontSize: 15, lineHeight: "22px", fontWeight: 400 },
  sectionTitle: { fontSize: 19, lineHeight: "25px", fontWeight: 650 },
  metricValue: { fontSize: 27, lineHeight: "33px", fontWeight: 700 },
  financeValue: { fontSize: 19, lineHeight: "26px", fontWeight: 600 },
  actionTitle: { fontSize: 15, lineHeight: "21px", fontWeight: 600 },
  body: { fontSize: 14, lineHeight: "20px", fontWeight: 400 },
  button: { fontSize: 13, lineHeight: "18px", fontWeight: 600 },
  table: { fontSize: 13, lineHeight: "19px", fontWeight: 400 },
  label: { fontSize: 12, lineHeight: "16px", fontWeight: 500 },
  chip: { fontSize: 11, lineHeight: "16px", fontWeight: 600 }
} as const satisfies Record<string, React.CSSProperties>

export const homeSize = {
  cardRadius: 10,
  controlRadius: 6,
  cardPadding: 18,
  sectionGap: 16,
  /** The gutter between the shell's sidebar and Home's first pixel. */
  contentGutter: 22,
  cardShadow: "0 1px 2px rgba(41, 52, 61, 0.04)"
} as const

export const homeCardStyle: React.CSSProperties = {
  background: homeColor.surface,
  border: `1px solid ${homeColor.border}`,
  borderRadius: homeSize.cardRadius,
  boxShadow: homeSize.cardShadow
}

/**
 * The desktop split: a flexible main column and a compact right rail. The rail
 * is clamped rather than a bare percentage so it stays readable on a laptop
 * and stops growing on a 2560 monitor.
 */
export const HOME_TWO_COLUMN =
  "grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_clamp(320px,27%,400px)]"
